/**
 * SubmitRating.jsx — Submit a star rating for a closed deal.
 *
 * Fields: star rating (1–5), optional comment.
 * Handles: RATING_WINDOW_CLOSED, RATING_ALREADY_SUBMITTED, DEAL_NOT_CLOSED.
 * If the viewer already rated (from GET deal), shows confirmation instead of the form.
 *
 * Requirements: 23.5
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Rating,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiClient } from '../../utils/auth';
import { hasViewerRatedHint, markViewerRatedDeal } from '../../utils/ratingHint';

const ERROR_MESSAGES = {
  RATING_WINDOW_CLOSED: 'The 7-day rating window for this deal has closed.',
  RATING_ALREADY_SUBMITTED: 'You have already submitted a rating for this deal.',
  DEAL_NOT_CLOSED: 'Ratings can only be submitted for CLOSED deals.',
};

export default function SubmitRating() {
  const { dealId } = useParams();
  const navigate = useNavigate();

  const [gate, setGate] = useState('loading'); // loading | form | already_rated | not_closed | load_error
  const [starValue, setStarValue] = useState(0);
  const [comments, setComments] = useState('');
  const [apiError, setApiError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/api/v1/deals/${dealId}/`);
        if (cancelled) return;
        if (data.viewer_has_submitted_rating) {
          setGate('already_rated');
        } else if (data.deal_status !== 'CLOSED') {
          setGate('not_closed');
        } else {
          setGate('form');
        }
      } catch {
        if (!cancelled) setGate('load_error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!starValue) {
      setApiError('Please select a star rating.');
      return;
    }

    setLoading(true);
    setApiError('');

    try {
      await apiClient.post('/api/v1/ratings/', {
        deal_id: Number(dealId),
        star_value: starValue,
        ...(comments && { comments }),
      });
      markViewerRatedDeal(dealId);
      setSubmitted(true);
    } catch (err) {
      const code = err.response?.data?.error?.code;
      setApiError(ERROR_MESSAGES[code] || 'Failed to submit rating. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (gate === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress aria-label="Loading" />
      </Box>
    );
  }

  if (gate === 'load_error') {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 6, p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load this deal. You may not be a party to it, or your session expired.
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/deals/mine')}>
          My deals
        </Button>
      </Box>
    );
  }

  if (gate === 'already_rated') {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 8, p: 3, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Rating already submitted
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          You have already rated the other party for this deal.
        </Typography>
        <Button variant="outlined" onClick={() => navigate(`/deals/${dealId}`)}>
          Back to deal
        </Button>
      </Box>
    );
  }

  if (gate === 'not_closed') {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 6, p: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Ratings are only available after this deal is <strong>CLOSED</strong> by an administrator.
        </Alert>
        <Button variant="outlined" onClick={() => navigate(`/deals/${dealId}`)}>
          Back to deal
        </Button>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 8, p: 3, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Rating Submitted
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Thank you for your feedback.
        </Typography>
        <Button variant="outlined" onClick={() => navigate(`/deals/${dealId}`)}>
          Back to Deal
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ maxWidth: 480, mx: 'auto', mt: 6, p: 3 }}
      noValidate
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/deals/${dealId}`)}
        sx={{
          mb: 3,
          borderColor: 'rgba(99, 102, 241, 0.4)',
          color: '#818cf8',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': {
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
          }
        }}
      >
        Back to Deal
      </Button>
      <Typography variant="h4" gutterBottom>
        Rate Your Experience
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Deal #{dealId}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography component="legend" gutterBottom>
          Star Rating *
        </Typography>
        <Rating
          name="star-rating"
          value={starValue}
          onChange={(_, newValue) => {
            setStarValue(newValue);
            setApiError('');
          }}
          size="large"
          aria-label="Star rating"
        />
      </Box>

      <TextField
        label="Comments (optional)"
        value={comments}
        onChange={(e) => setComments(e.target.value)}
        fullWidth
        multiline
        rows={4}
        margin="normal"
        inputProps={{ 'aria-label': 'Rating comments' }}
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{ mt: 3 }}
        aria-label="Submit rating"
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Rating'}
      </Button>
      {apiError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {apiError}
        </Alert>
      )}
    </Box>
  );
}
